import React, { useState } from "react";
import { Modal } from "seed/helpers";
import l from "components/util/l";
import PermissionGridField from "components/util/helpers/user/permissions/PermissionField";
import CustomPermissionForm from "components/util/helpers/user/permissions/PermissionField.sub.CustomPermissionForm.view";
import { omitKey } from "components/util/helpers/user/permissions/PermissionField.lib.customPermission";
import { getViewPermissionKey } from "components/util/views";
import { PERMISSIONS, FLOW_PERMISSION_NAMES } from "components/util/permissions";

const GeneralPermissionField = ({
  flowId,
  onlyAdminRoles,
  values,
  details,
  views,
  setFieldValue,
  setValues,
  setDetails
}) => {

  // null while closed; { originalKey, label, group } for the permission being added or edited.
  const [formPermission, setFormPermission] = useState(null);

  const standardNames = getStandardNames(views);

  const onAddCustom = () =>
    setFormPermission({ originalKey: null, label: "", group: "" });

  const onEditCustom = (name) =>
    setFormPermission({ originalKey: name, ...details[name] });

  const onDeleteCustom = (name) => {
    setValues(omitKey(values, name));
    setDetails(omitKey(details, name));
  };

  // A renamed key keeps each role's switch; a new one starts denied for every role, with an
  // explicit entry per role so the backend persists a row for each.
  const onSaveCustom = ({ key, label, group }) => {
    const { originalKey } = formPermission;
    const statuses = originalKey
      ? values[originalKey]
      : Object.fromEntries(getRoleNames(values).map((role) => [role, false]));
    setValues({ ...omitKey(values, originalKey), [key]: statuses });
    setDetails({ ...omitKey(details, originalKey), [key]: { label, group } });
    setFormPermission(null);
  };

  const fields = groupFields([
    ...standardNames.map((name) => buildField(name, details)),
    ...getCustomFields(values, details, standardNames, onEditCustom, onDeleteCustom),
  ]);

  return (
    <>
      <PermissionGridField
        flowId={flowId}
        onlyAdminRoles={onlyAdminRoles}
        fields={fields}
        values={values}
        setFieldValue={setFieldValue}
        setValues={setValues}
      />

      <button type="button" className="btn btn-sm btn-outline-primary" onClick={onAddCustom}>
        <i className="fas fa-plus mr-1"></i>{l("Nuevo permiso personalizado%PA892")}
      </button>

      {formPermission &&
        <Modal
          width="500"
          height="460"
          permission={formPermission}
          takenKeys={Object.keys(values)}
          onSave={onSaveCustom}
          onClose={() => setFormPermission(null)}
          component={CustomPermissionForm}
        />}
    </>
  );

};

// The built-ins the editor exposes, in the order they read best: the brief's tabs close them, and
// the project's own permissions come after all of them. Which sections appear depends on the views
// the flow has; show_views is left out because nothing checks it.
const getStandardNames = (views = []) => [
  PERMISSIONS.SHOW_SIDENAV,
  PERMISSIONS.SHOW_TOPNAV,
  ...new Set(views.map(getViewPermissionKey).filter(Boolean)),
  PERMISSIONS.SHOW_SHARE,
  PERMISSIONS.SHOW_MANAGE_COLLECTIONS,
  PERMISSIONS.SHOW_FLOW_MANAGER,
  PERMISSIONS.SHOW_ENVIRONMENTS,
  PERMISSIONS.SHOW_GOVERNANCE,
  PERMISSIONS.SHOW_ACTIVITY,
  PERMISSIONS.SHOW_ASSISTANT,
  PERMISSIONS.SHOW_BRIEF_ADVISOR,
  PERMISSIONS.SHOW_BRIEF_DEFINITION,
  PERMISSIONS.SHOW_BRIEF_SPECIFICATION,
  PERMISSIONS.SHOW_BRIEF_DEVELOPMENT,
  PERMISSIONS.SHOW_BRIEF_QA,
  PERMISSIONS.SHOW_BRIEF_INTERACTIONS,
];

// Custom permissions are the flow-level keys that are not built-in: the project's show_<view>
// keys and whatever the admin or the project's permissions.csv declared.
const getCustomFields = (values, details, standardNames, onEdit, onDelete) => {

  const knownNames = new Set([...standardNames, ...FLOW_PERMISSION_NAMES]);

  return Object.keys(values)
    .filter((name) => !knownNames.has(name))
    .map((name) => ({
      ...buildField(name, details),
      editTitle: l("Editar%GA002"),
      onEdit: () => onEdit(name),
      deleteTitle: l("Eliminar%GA001"),
      onDelete: () => onDelete(name),
    }))
    .sort((first, second) =>
      first.group.localeCompare(second.group) || first.label.localeCompare(second.label));

};

const buildField = (name, details) => ({
  name,
  label: getPermissionLabel(name, details),
  group: getPermissionGroup(name, details),
});

// Labels may be inline locales (%%es:…;;en:…%%), which l() resolves; plain text passes through.
// A view key the API has no row for yet has no details, so it reads from its own key.
const getPermissionLabel = (name, details) =>
  details[name]?.label ? l(details[name].label) : humanizeKey(name);

const getPermissionGroup = (name, details) =>
  details[name]?.group ? l(details[name].group) : l("Permisos propios%VG373");

// Rows of one group stay together, in the order their group first appears; the first row of each
// group carries the header.
const groupFields = (fields) => {
  const groups = [...new Set(fields.map((field) => field.group))];
  return groups.flatMap((group) =>
    fields
      .filter((field) => field.group === group)
      .map((field, index) => ({ ...field, separator: index === 0 ? group : undefined }))
  );
};

const humanizeKey = (name) => {
  const words = name.replace(/^show_/, "").replace(/_+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// Every role present in the current map.
const getRoleNames = (values = {}) =>
  [...new Set(Object.values(values).flatMap((roleMap) => Object.keys(roleMap || {})))];

export default GeneralPermissionField;
